import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { proposalGenerationService, proposalsService, briefingsService, GenerationReadiness, ProposalDocument, Briefing } from '../services/api';
import LeadBriefingTab from './LeadBriefingTab';
import { mensagemDoErroDeCarga, lerSecao, falhaIgnoradaDeProposito, campo } from '../design';
import { fmtDate, fmtDateTime } from '../utils/formatters';
import CascaDeModal from './shared/CascaDeModal';
import { useConfirm } from './shared/ConfirmDialog';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { marcarComoEnviada } from '../utils/crm';

/**
 * Largura que o documento de proposta PEDE, em px.
 *
 * ⛔ Não é só a folha (15/09/2026). A folha A4 tem 210mm = 794px a 96dpi, mas o
 * documento traz `body{padding:14px 13px}` em volta dela, e ainda pode aparecer
 * barra de rolagem vertical no iframe. Medir só a folha foi o erro que fez o
 * conserto apertar o problema: com o iframe fixado em 794 o documento passou a
 * caber em MENOS espaço do que pedia, e o excedente sumiu no `overflow:hidden`
 * da `.page`, sem barra que denunciasse.
 *
 *     794 (folha) + 26 (padding do body) + 20 (folga p/ scrollbar) = 840
 *
 * A prévia escala isto para a largura real do painel. Sobrar é inofensivo: dá
 * margem branca. Faltar corta o documento e faz parecer defeito de layout.
 */
const A4_LARGURA_PX = 840;

/**
 * Workspace da proposta gerada pela IA (fases 4 e 5 do gerador), organizado
 * pelo ciclo de vida: Briefing → Versões → Envio → Resultado. A etapa ativa
 * é derivada do estado da proposta; o usuário pode olhar qualquer uma.
 *
 * Regras que a tela obedece, não inventa: sem briefing completo não gera; sem
 * gate aprovado não publica nem envia; publicar troca o documento do link
 * público; a Etapa 3 do briefing acompanha o status (o backend grava
 * enviada/fechou/perdeu; o vendedor completa objeção e aprendizado aqui).
 */

interface Props {
  proposal: any;
  /**
   * O lead e a permissão vêm de fora porque o briefing é do LEAD, não da
   * proposta: a mesma dor, as mesmas quantidades e as mesmas provas valem para
   * todas as versões geradas. Vinham da aba fixa do lead até 26/09/2026, e é de
   * lá que `canEdit` precisa continuar chegando — sem ele, quem só tem leitura
   * abriria o editor com os campos liberados.
   */
  leadId: number;
  leadData?: any;
  canEdit?: boolean;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  onSendEmail?: (proposal: any) => void;
}

type Stage = 'briefing' | 'versoes' | 'envio' | 'resultado';

const GATE: Record<string, { cls: string; label: string }> = {
  aprovado: { cls: 'bg-green-500/15 text-green-400 border-green-500/40', label: 'aprovada no gate' },
  aviso: { cls: 'bg-amber-500/15 text-amber-400 border-amber-500/40', label: 'aprovada com avisos' },
  bloqueado: { cls: 'bg-red-500/15 text-red-400 border-red-500/40', label: 'bloqueada no gate' },
  erro: { cls: 'bg-red-500/15 text-red-400 border-red-500/40', label: 'erro na geração' },
};
const INPUT = `w-full px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none placeholder:text-slate-600`;
const BTN = 'px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-200 text-xs font-medium rounded-lg';
const BTN_PRIMARY = 'px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-50 text-white text-sm font-medium rounded-lg';

export default function ProposalGenerationModal({ proposal: proposalInicial, leadId, leadData, canEdit = true, onClose, onChanged, onSendEmail }: Props) {
  const confirmar = useConfirm();
  const [proposal, setProposal] = useState<any>(proposalInicial);
  const [readiness, setReadiness] = useState<GenerationReadiness | null>(null);
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  // Não null: a leitura do briefing falhou. Sem isto a tela dizia "Sem briefing ligado" e "não preenchido", que são
  // afirmações sobre o lead que ela não tinha como fazer (07/10/2026).
  const [briefingNaoCarregou, setBriefingNaoCarregou] = useState<string | null>(null);
  const [propostaNaoAtualizou, setPropostaNaoAtualizou] = useState<string | null>(null);
  /**
   * Grava o que o editor de briefing ainda não salvou.
   *
   * O briefing deixou de ter aba própria no lead e passou a viver aqui, atrás
   * do "Gerar com IA" — e veio junto o motivo de este ref existir: em 05/09 o
   * briefing da SOS se perdeu em silêncio porque o pai fechou sem descarregar o
   * que estava digitado. Aqui os dois caminhos de saída chamam o flush: fechar
   * o modal e mandar gerar. Gerar sem descarregar seria pior que perder texto —
   * a IA geraria a partir da versão velha, e ninguém saberia por quê.
   */
  const briefingFlushRef = useRef<(() => Promise<void>) | null>(null);

  /** Grava o que o editor ainda não salvou. Devolve o MOTIVO quando não conseguiu, ou null. */
  const descarregarBriefing = async (): Promise<string | null> => {
    try { await briefingFlushRef.current?.(); return null; } catch (e) { return mensagemDoErroDeCarga(e); }
  };

  /**
   * ⛔ Sair do briefing com texto que NÃO gravou pergunta antes (08/10/2026). Antes, o flush engolia a falha e o modal
   * fechava (ou trocava de etapa): o editor desmontava com o aviso de erro dentro, e o texto se perdia calado. É a
   * classe do briefing da SOS. Devolve true quando pode sair.
   */
  const sairDoBriefing = async (): Promise<boolean> => {
    const motivo = await descarregarBriefing();
    if (!motivo) return true;
    return confirmar({
      title: 'O briefing não foi salvo',
      message: `A última gravação falhou (${motivo}).`,
      consequence: 'Sair agora perde o que foi escrito desde a última gravação. Fique para tentar salvar de novo.',
      danger: true,
      confirmLabel: 'Sair e perder',
      cancelLabel: 'Ficar',
    });
  };

  const fechar = async () => { if (await sairDoBriefing()) onClose(); };
  const irParaEtapa = async (k: Stage) => {
    if (stage === 'briefing' && k !== 'briefing' && !(await sairDoBriefing())) return;
    setStage(k);
  };

  const registrarFlushDoBriefing = useCallback((fn: (() => Promise<void>) | null) => {
    briefingFlushRef.current = fn;
  }, []);

  /**
   * O briefing gravou: o "Gerar proposta" está NESTA tela, e é `readiness` que
   * o libera. Sem reconsultar aqui, quem acabasse de preencher o campo que
   * faltava continuaria olhando o botão apagado com o motivo antigo no title —
   * e o único jeito de destravar seria fechar e reabrir o modal.
   */
  const briefingGravado = useCallback(async (b: Briefing) => {
    setBriefing(b);
    try { setReadiness(await proposalGenerationService.readiness(proposalInicial.id)); } catch { /* o botão só fica como estava */ }
  }, [proposalInicial.id]);
  const [docs, setDocs] = useState<ProposalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<{ id: string; step: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [selected, setSelected] = useState<ProposalDocument | null>(null);
  const [previewScale, setPreviewScale] = useState(1);
  const roRef = useRef<ResizeObserver | null>(null);
  // ⛔ Callback ref, não useRef + useEffect (15/09/2026): o painel da prévia só
  // é montado DEPOIS que uma versão é selecionada, então um efeito com deps []
  // roda antes dele existir, encontra null, sai na primeira linha e a escala
  // fica em 1 para sempre. Foi o que fez a folha continuar cortada mesmo com o
  // conserto no ar. O callback ref dispara quando o nó entra e quando sai.
  const previewRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const medir = () => setPreviewScale(Math.min(1, (el.clientWidth || A4_LARGURA_PX) / A4_LARGURA_PX));
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    roRef.current = ro;
  }, []);
  useEffect(() => () => roRef.current?.disconnect(), []);
  const [urls, setUrls] = useState<{ htmlUrl: string | null; pdfUrl: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<Stage | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [resultado, setResultado] = useState<Record<string, any>>({});
  const [resultadoDirty, setResultadoDirty] = useState(false);
  const pollRef = useRef<number | null>(null);

  const publicadaId = proposal.currentDocumentId as number | null;
  const decidida = proposal.status === 'Aceita' || proposal.status === 'Recusada';
  const custoTotal = docs.reduce((s, d) => s + (Number(d.costUsd) || 0), 0);

  const load = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const [r, d, p, lidoDoBriefing] = await Promise.all([
        proposalGenerationService.readiness(proposal.id),
        proposalGenerationService.documents(proposal.id),
        proposalsService.getById ? lerSecao(proposalsService.getById(proposal.id)) : Promise.resolve({ valor: null, erro: null as string | null }),
        proposal.leadId
          ? briefingsService.get(proposal.leadId).then((b) => ({ b, erro: null as string | null }), (e) => ({ b: null, erro: mensagemDoErroDeCarga(e) }))
          : Promise.resolve({ b: null, erro: null as string | null }),
      ]);
      setReadiness(r); setDocs(d);
      // Sem a leitura nova, a tela segue com a proposta de quando abriu (status, versão publicada) e diz isso.
      setPropostaNaoAtualizou(p.erro);
      if (p.valor) setProposal(p.valor);
      const b = lidoDoBriefing.b;
      setBriefingNaoCarregou(lidoDoBriefing.erro);
      if (b) { setBriefing(b); if (!resultadoDirty) setResultado(b.resultado || {}); }
      setSelected((prev) => prev ? d.find((x) => x.id === prev.id) || d[0] || null : d.find((x) => x.id === ((p.valor as any)?.currentDocumentId ?? proposal.currentDocumentId)) || d[0] || null);
    } catch (e: any) { setErro(e?.response?.data?.message || 'Erro ao carregar'); }
    finally { setLoading(false); }
  }, [proposal.id, proposal.leadId, proposal.currentDocumentId, resultadoDirty]);

  useEffect(() => { load(); return () => { if (pollRef.current) window.clearInterval(pollRef.current); }; }, [load]);

  // Etapa derivada do estado; só na primeira carga, para não roubar o clique do usuário.
  useEffect(() => {
    if (stage !== null || loading || !readiness) return;
    if (decidida) setStage('resultado');
    else if (publicadaId || proposal.status === 'Enviada' || proposal.status === 'Visualizada') setStage('envio');
    else if (docs.length > 0 || readiness.ok) setStage('versoes');
    else setStage('briefing');
  }, [stage, loading, readiness, decidida, publicadaId, proposal.status, docs.length]);

  useEffect(() => {
    if (!selected) { setUrls(null); return; }
    let cancel = false;
    setUrls(null);
    if (selected.htmlS3Key || selected.pdfS3Key) {
      proposalGenerationService.urls(selected.id).then((u) => { if (!cancel) setUrls(u); }).catch(() => { if (!cancel) setUrls({ htmlUrl: null, pdfUrl: null }); });
    }
    return () => { cancel = true; };
  }, [selected]);

  const gerar = async () => {
    setErro(null);
    // Antes de qualquer coisa: o que está escrito vira o que o servidor lê. Se não gravou, NÃO gera: a IA geraria a
    // partir da versão velha, e ninguém saberia por quê.
    const naoGravou = await descarregarBriefing();
    if (naoGravou) { setErro(`Não gerei: o briefing não foi salvo (${naoGravou}). Salve o briefing e gere de novo.`); return; }
    try {
      const { jobId } = await proposalGenerationService.start(proposal.id);
      setJob({ id: jobId, step: 'Preparando contexto' });
      setStage('versoes');
      pollRef.current = window.setInterval(async () => {
        // ⛔ Dois casos que o catch antigo ("tenta no próximo tick") misturava (08/10/2026): antes de terminar, o
        // intervalo segue e a próxima consulta refaz; DEPOIS de terminar o intervalo já foi desligado, e a falha ao
        // carregar a versão nova não era tentada de novo nem dita: a versão não aparecia, calada.
        let terminou = false;
        try {
          const j = await proposalGenerationService.job(jobId);
          if (j.status === 'running') { setJob({ id: jobId, step: j.step || '' }); return; }
          terminou = true;
          if (pollRef.current) window.clearInterval(pollRef.current);
          setJob(null);
          if (j.status === 'error') setErro(j.error || 'A geração falhou');
          if (j.status === 'not_found') setErro('A geração não foi encontrada no servidor (reiniciou?). Confira a lista de versões.');
          await load();
          if (j.status === 'done' && j.documentId) {
            const d = await proposalGenerationService.documents(proposal.id);
            setDocs(d); setSelected(d.find((x) => x.id === j.documentId) || d[0] || null);
          }
        } catch (e) {
          if (terminou) {
            setJob(null);
            setErro(`A geração terminou, mas não consegui carregar a versão nova (${mensagemDoErroDeCarga(e)}). Feche e abra o modal para vê-la.`);
          } else {
            falhaIgnoradaDeProposito('consulta do andamento da geração: o intervalo segue ligado e a próxima consulta, em 4 s, refaz')(e);
          }
        }
      }, 4000);
    } catch (e: any) { setErro(e?.response?.data?.message || 'Não foi possível iniciar a geração'); }
  };

  const publicar = async () => {
    if (!selected) return;
    if (!(await confirmar({ title: `Publicar a versão ${selected.version}?`, message: 'Ela passa a ser o documento do link público desta proposta.', consequence: 'Quem abrir o link a partir de agora vê esta versão.', confirmLabel: 'Publicar' }))) return;
    setBusy(true); setErro(null);
    try {
      const p = await proposalGenerationService.approve(selected.id);
      setProposal(p);
      await onChanged();
      await load();
      setStage('envio');
    } catch (e: any) { setErro(e?.response?.data?.message || 'Não foi possível publicar'); }
    finally { setBusy(false); }
  };

  const mudarStatus = async (status: string, confirmMsg: string) => {
    if (!(await confirmar({ title: `Marcar como ${status}?`, message: confirmMsg, confirmLabel: `Marcar como ${status}` }))) return;
    setBusy(true); setErro(null);
    try {
      const p = await proposalsService.updateStatus(proposal.id, status);
      setProposal(p);
      await onChanged();
      await load();
    } catch (e: any) { setErro(e?.response?.data?.message || 'Não foi possível mudar o status'); }
    finally { setBusy(false); }
  };

  const publicLink = useMemo(() => {
    const params = new URLSearchParams();
    if (proposal.updatedAt) params.set('v', String(new Date(proposal.updatedAt).getTime()));
    const qs = params.toString();
    return `${window.location.origin}/p/${proposal.code}${qs ? `?${qs}` : ''}`;
  }, [proposal.code, proposal.updatedAt]);

  const copiarLink = async () => {
    try { await navigator.clipboard.writeText(publicLink); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }
    catch { setErro('Não foi possível copiar; selecione o link e copie à mão.'); }
  };

  const salvarResultado = async () => {
    if (!proposal.leadId) return;
    setBusy(true); setErro(null);
    try {
      const b = await briefingsService.save(proposal.leadId, { resultado });
      setBriefing(b); setResultado(b.resultado || {}); setResultadoDirty(false);
    } catch (e: any) { setErro(e?.response?.data?.message || 'Não foi possível salvar o resultado'); }
    finally { setBusy(false); }
  };

  const publicada = selected && publicadaId === selected.id;
  const podePublicar = selected && (selected.gateStatus === 'aprovado' || selected.gateStatus === 'aviso') && !publicada && !decidida;
  const versaoPublicada = docs.find((d) => d.id === publicadaId);
  // G1 (28/10/2026): a mesma trava do cartão; aqui a contagem de versões é a lista que o modal já carregou.
  const marcar = marcarComoEnviada({ ...proposal, versoesGeradas: docs.length });
  const pendencias = briefing?.pendencias?.filter((p) => p.etapa < 3) || [];

  const stages: Array<{ k: Stage; label: string; done: boolean; hint: string }> = [
    { k: 'briefing', label: 'Briefing', done: !!briefing?.prontoParaGerar, hint: briefing ? (briefing.prontoParaGerar ? 'completo' : `${pendencias.length} a confirmar`) : briefingNaoCarregou ? 'não carregou' : 'não preenchido' },
    { k: 'versoes', label: 'Versões', done: docs.some((d) => d.gateStatus === 'aprovado' || d.gateStatus === 'aviso'), hint: docs.length ? `${docs.length} versão(ões)` : 'nenhuma' },
    { k: 'envio', label: 'Envio', done: ['Enviada', 'Visualizada', 'Aceita', 'Recusada'].includes(proposal.status), hint: versaoPublicada ? `v${versaoPublicada.version} publicada` : (proposal.customHtmlS3Key || proposal.customPdfS3Key) ? 'documento externo' : 'nada publicado' },
    { k: 'resultado', label: 'Resultado', done: resultado.resultado === 'fechou' || resultado.resultado === 'perdeu', hint: resultado.resultado === 'fechou' ? 'fechou' : resultado.resultado === 'perdeu' ? 'perdeu' : 'em aberto' },
  ];

  return (
    // Assistente em quatro etapas. Altura FIXA (85vh): Briefing, Versões, Envio e
    // Resultado têm tamanhos muito diferentes e a caixa saltava a cada troca, como
    // no Editar Fatura. Não fecha ao clicar fora: o briefing tem texto digitado.
    <CascaDeModal
      largura='xl'
      altura='fixa'
      zIndex='z-[60]'
      titulo={`${proposal.code} · ${proposal.clientTradeName || proposal.clientName}`}
      subtitulo={`${proposal.status}${readiness?.rulebookVersion ? ` · núcleo v${readiness.rulebookVersion}` : ''}${custoTotal > 0 ? ` · IA: US$ ${custoTotal.toFixed(2)} em ${docs.length} versão(ões)` : ''}`}
      onClose={fechar}
      subCabecalho={
        // Mesma gramática da barra do funil (#249): feita = check em success,
        // ATIVA = o ring laranja da etapa atual, futuras neutras.
        <div className='flex gap-1 px-3 py-2 border-b border-central-borda'>
          {stages.map((s, i) => (
            <button key={s.k} onClick={() => irParaEtapa(s.k as Stage)} className={`flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg text-left transition-colors ${stage === s.k ? 'ring-2 ring-orange-400 bg-slate-800' : 'hover:bg-slate-800/60'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${s.done ? TONE_BADGE.success : 'bg-slate-700 text-slate-300'}`}>{s.done ? '✓' : i + 1}</span>
              <span className='min-w-0'>
                <span className={`block text-sm font-medium ${stage === s.k ? 'text-white' : 'text-slate-300'}`}>{s.label}</span>
                <span className='block text-[11px] text-slate-500 truncate'>{s.hint}</span>
              </span>
            </button>
          ))}
        </div>
      }
      topo={(erro || loading) ? (
        <>
          {erro && <div className={`mx-5 mt-3 text-xs border rounded-lg px-3 py-2 ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`}>{erro}</div>}
          {loading && <div className='px-5 py-2 text-xs text-slate-500'>Carregando…</div>}
        </>
      ) : undefined}
      corpoClassName='flex flex-col'
      rodape={
        <div className='ml-auto flex items-center gap-2'>
          {job ? (
            <span className={`flex items-center gap-2 text-xs border rounded-lg px-3 py-2 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`}>
              <span className='inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin'></span>{job.step}…
            </span>
          ) : !decidida && (
            <button onClick={gerar} disabled={!readiness?.ok || loading || busy}
              title={readiness && !readiness.ok ? readiness.motivos.join(' · ') : 'Gera uma versão nova a partir do briefing e do orçamento'}
              className={BTN_PRIMARY}>
              {docs.length ? 'Gerar nova versão' : 'Gerar proposta'}
            </button>
          )}
        </div>
      }
    >
        {/* ── Briefing ── */}
        {propostaNaoAtualizou && !loading && (
          <div role='alert' className='mx-5 mt-3 px-3 py-2 rounded-lg border border-amber-600/50 bg-amber-500/10 text-xs text-amber-200' data-proposta='nao-atualizou'>
            Não consegui atualizar os dados da proposta ({propostaNaoAtualizou}): o status e a versão publicada mostrados são os de quando esta tela abriu.
          </div>
        )}
        {stage === 'briefing' && !loading && (
          <div className='flex-1 min-h-0 overflow-y-auto p-5 space-y-4'>
            {readiness && !readiness.ok && (
              <div className='bg-central-elevado/50 border border-amber-500/30 rounded-xl p-3'>
                <p className='text-[11px] font-mono uppercase tracking-wide text-amber-400 mb-1'>Antes de gerar</p>
                <ul className='text-xs text-slate-300 space-y-0.5'>{readiness.motivos.map((m, i) => <li key={i}>· {m}</li>)}</ul>
              </div>
            )}
            {readiness?.ok && <div className='text-xs text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2'>Pronto para gerar: briefing completo, itens do catálogo, núcleo v{readiness.rulebookVersion} ativo.</div>}
            {/* O EDITOR, não mais um resumo com "vá para a aba Briefing".
                O briefing deixou de ser aba fixa do lead — parecia etapa
                obrigatória para todo mundo, quando é pré-requisito de um
                caminho só: quem monta por catálogo nunca precisa, quem gera por
                IA descobre aqui, no momento em que precisa. */}
            <LeadBriefingTab
              leadId={leadId}
              leadData={leadData || { tradeName: proposal.clientTradeName, companyName: proposal.clientName }}
              canEdit={canEdit}
              mostrarEtapa3={false}
              registerFlush={registrarFlushDoBriefing}
              onSaved={briefingGravado}
            />
          </div>
        )}

        {/* ── Versões ── */}
        {stage === 'versoes' && (
          <div className='flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[340px_1fr]'>
            <div className='border-r border-central-borda overflow-y-auto p-4 space-y-2'>
              <p className='text-[11px] font-mono uppercase tracking-wide text-slate-500'>Versões ({docs.length})</p>
              {!loading && docs.length === 0 && !job && <p className='text-xs text-slate-500'>Nenhuma versão ainda. A geração leva de um a dois minutos.</p>}
              {docs.map((d) => {
                const g = GATE[d.gateStatus] || GATE.erro;
                return (
                  <button key={d.id} onClick={() => setSelected(d)} className={`w-full text-left rounded-xl border p-3 transition-colors ${selected?.id === d.id ? 'bg-orange-500/5 border-orange-500/50 ring-1 ring-orange-500/40' : 'bg-slate-800/50 border-slate-700 hover:border-slate-500'}`}>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='font-mono font-bold text-white'>v{d.version}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${g.cls}`}>{g.label}</span>
                    </div>
                    <p className='text-[11px] text-slate-500 mt-1'>{fmtDateTime(d.createdAt)}{d.gateReport?.paginas ? ` · ${d.gateReport.paginas} pág.` : ''}{d.costUsd != null ? ` · US$ ${Number(d.costUsd).toFixed(2)}` : ''}</p>
                    {publicadaId === d.id && <p className='text-[11px] text-green-400 mt-1'>publicada no link público</p>}
                  </button>
                );
              })}
            </div>
            {/* min-w-0: coluna 1fr de grid tem min-width auto e nao encolhe
                abaixo do conteudo. Com a folha A4 de 794px dentro, a celula
                virava 794px e a medicao da previa devolvia 794 (15/09/2026). */}
            <div className='min-h-0 min-w-0 flex flex-col'>
              {!selected ? (
                <div className='flex-1 flex items-center justify-center text-sm text-slate-500 p-6 text-center'>{job ? 'Escrevendo, montando e conferindo a proposta…' : 'Selecione uma versão, ou gere a primeira.'}</div>
              ) : (
                <>
                  <div className='p-4 border-b border-central-borda space-y-2'>
                    <div className='flex items-center justify-between gap-3 flex-wrap'>
                      <div className='text-sm text-slate-300'><span className='font-mono font-bold text-white'>v{selected.version}</span><span className='text-slate-500'> · núcleo v{selected.rulebookVersion || '?'} · {selected.model || 'modelo ?'}</span></div>
                      <div className='flex gap-2'>
                        {urls?.pdfUrl && <a href={urls.pdfUrl} target='_blank' rel='noopener noreferrer' className={BTN}>Abrir PDF</a>}
                        {urls?.htmlUrl && <a href={urls.htmlUrl} target='_blank' rel='noopener noreferrer' className={BTN}>Abrir HTML</a>}
                        {podePublicar && <button onClick={publicar} disabled={busy} className='px-3 py-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg'>{busy ? 'Publicando…' : 'Publicar esta versão'}</button>}
                        {publicada && <span className='px-3 py-1.5 text-xs text-green-400'>✓ publicada</span>}
                      </div>
                    </div>
                    {(selected.gateReport?.falhas?.length > 0 || selected.gateReport?.avisos?.length > 0) && (
                      <div className={`rounded-lg border px-3 py-2 ${selected.gateReport.falhas?.length ? 'border-red-500/40 bg-red-500/10' : 'border-amber-500/40 bg-amber-500/10'}`}>
                        {selected.gateReport.falhas?.length > 0 && (<>
                          <p className='text-[11px] font-mono uppercase tracking-wide text-red-400'>Falhas ({selected.gateReport.falhas.length}) · corrija o briefing ou o núcleo e gere de novo</p>
                          <ul className='text-xs text-slate-300 mt-1 space-y-0.5'>{selected.gateReport.falhas.map((f, i) => <li key={i}>· {f}</li>)}</ul>
                        </>)}
                        {selected.gateReport.avisos?.length > 0 && (<>
                          <p className='text-[11px] font-mono uppercase tracking-wide text-amber-400 mt-1'>Avisos ({selected.gateReport.avisos.length})</p>
                          <ul className='text-xs text-slate-400 mt-1 space-y-0.5'>{selected.gateReport.avisos.map((a, i) => <li key={i}>· {a}</li>)}</ul>
                        </>)}
                      </div>
                    )}
                  </div>
                  <div ref={previewRef} className='flex-1 min-h-0 min-w-0 overflow-hidden bg-[#20242b]'>
                    {!selected.htmlS3Key && !selected.pdfS3Key && <div className='h-full flex items-center justify-center text-xs text-slate-500 p-6 text-center'>Sem arquivo para esta versão: veja as falhas acima e gere de novo.</div>}
                    {urls === null && (selected.htmlS3Key || selected.pdfS3Key) && <div className='h-full flex items-center justify-center text-xs text-slate-500'>Carregando prévia…</div>}
                    {urls?.htmlUrl && (
                      <iframe
                        title={`Proposta v${selected.version}`}
                        src={urls.htmlUrl}
                        className='border-0'
                        style={{
                          width: A4_LARGURA_PX,
                          height: `${100 / (previewScale || 1)}%`,
                          transform: `scale(${previewScale})`,
                          transformOrigin: 'top left',
                        }}
                      />
                    )}
                    {urls && !urls.htmlUrl && (selected.htmlS3Key || selected.pdfS3Key) && <div className='h-full flex items-center justify-center text-xs text-slate-500 p-6 text-center'>Prévia indisponível: use "Abrir PDF" acima.</div>}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Envio ── */}
        {stage === 'envio' && !loading && (
          <div className='flex-1 min-h-0 overflow-y-auto p-5 space-y-4'>
            <div className='bg-central-elevado/50 border border-central-borda rounded-xl p-4'>
              <p className='text-[11px] font-mono uppercase tracking-wide text-slate-500 mb-2'>Documento do link público</p>
              {versaoPublicada ? (
                <p className='text-sm text-white'>Versão <b>v{versaoPublicada.version}</b> gerada pelo HUB, {GATE[versaoPublicada.gateStatus]?.label}, publicada em {fmtDateTime(versaoPublicada.createdAt)}.</p>
              ) : (proposal.customHtmlS3Key || proposal.customPdfS3Key) ? (
                <p className='text-sm text-slate-300'>Documento externo anexado à mão. Nenhuma versão gerada foi publicada: para trocar, publique uma na etapa Versões.</p>
              ) : (
                <p className='text-sm text-amber-400'>Nada publicado ainda. O link mostra o layout nativo da proposta. Publique uma versão aprovada na etapa Versões.</p>
              )}
            </div>
            <div className='bg-central-elevado/50 border border-central-borda rounded-xl p-4 space-y-3'>
              <p className='text-[11px] font-mono uppercase tracking-wide text-slate-500'>Link público</p>
              <div className='flex gap-2'>
                <input readOnly value={publicLink} className={`${INPUT} font-mono text-xs`} onFocus={(e) => e.currentTarget.select()} />
                <button onClick={copiarLink} className={BTN}>{linkCopied ? 'Copiado ✓' : 'Copiar'}</button>
                <a href={publicLink} target='_blank' rel='noopener noreferrer' className={BTN}>Abrir</a>
              </div>
              <div className='flex gap-2 flex-wrap'>
                {onSendEmail && !decidida && <button onClick={() => onSendEmail(proposal)} className='px-3 py-1.5 bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 text-xs font-medium rounded-lg'>📧 Enviar por e-mail</button>}
                {marcar.pode && <button disabled={busy} onClick={() => mudarStatus('Enviada', 'Marcar como Enviada? Use quando o link foi mandado por WhatsApp ou em mão, fora do e-mail do HUB.')} className={BTN}>Marcar como Enviada</button>}
              </div>
              {marcar.frase && <p className={`text-xs ${TONE_TEXT.warning}`} data-trava-do-gate>{marcar.frase}</p>}
              <p className='text-[11px] text-slate-500'>Proposta gerada pelo HUB só vira Enviada com uma versão aprovada no gate publicada, ou com documento externo anexado. O e-mail do HUB muda o status sozinho.</p>
            </div>
            <div className='bg-central-elevado/50 border border-central-borda rounded-xl p-4'>
              <p className='text-[11px] font-mono uppercase tracking-wide text-slate-500 mb-2'>Rastro</p>
              <ul className='text-xs text-slate-300 space-y-0.5'>
                <li>Status: <b className='text-white'>{proposal.status}</b></li>
                {proposal.sentAt && <li>Enviada em {fmtDateTime(proposal.sentAt)}</li>}
                {proposal.viewedAt && <li>Visualizada em {fmtDateTime(proposal.viewedAt)}</li>}
                {proposal.acceptedAt && <li className='text-green-400'>Aceita em {fmtDateTime(proposal.acceptedAt)}{proposal.acceptedByName ? ` por ${proposal.acceptedByName}` : ''}</li>}
                {proposal.rejectedAt && <li className='text-red-400'>Recusada em {fmtDateTime(proposal.rejectedAt)}{proposal.rejectionReason ? ` · ${proposal.rejectionReason}` : ''}</li>}
                <li>Válida até {proposal.validUntil ? fmtDate(proposal.validUntil) : 'não definida'}</li>
              </ul>
            </div>
          </div>
        )}

        {/* ── Resultado (Etapa 3) ── */}
        {stage === 'resultado' && !loading && (
          <div className='flex-1 min-h-0 overflow-y-auto p-5 space-y-4'>
            {!briefing?.id && briefingNaoCarregou ? (
              <div role='alert' className='text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2' data-briefing='nao-carregou'>
                Não consegui carregar o briefing deste lead ({briefingNaoCarregou}). Isto não quer dizer que ele não existe: o resultado fica fechado até a leitura dar certo.
                <button type='button' onClick={load} className='ml-2 px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs font-medium'>Tentar de novo</button>
              </div>
            ) : !briefing?.id ? (
              <p className='text-sm text-slate-400'>Sem briefing ligado a esta proposta: a Etapa 3 vive no briefing do lead.</p>
            ) : (
              <>
                <div className='flex items-start justify-between gap-3 flex-wrap'>
                  <div>
                    <p className='text-sm font-semibold text-white'>Pós-envio</p>
                    <p className='text-xs text-slate-500'>Enviada, aceita e recusada são gravadas sozinhas pelo status. O que só você sabe é a objeção e o aprendizado; padrão recorrente vira atualização do núcleo.</p>
                  </div>
                  <button onClick={salvarResultado} disabled={busy || !resultadoDirty} className={BTN_PRIMARY}>{busy ? 'Salvando…' : resultadoDirty ? 'Salvar resultado' : 'Salvo'}</button>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-xs text-slate-400 mb-1'>Resultado</label>
                    <select value={resultado.resultado || 'em_aberto'} onChange={(e) => { setResultado({ ...resultado, resultado: e.target.value }); setResultadoDirty(true); }} className={INPUT}>
                      <option value='em_aberto'>Em aberto</option><option value='fechou'>Fechou</option><option value='perdeu'>Perdeu</option>
                    </select>
                  </div>
                  <div>
                    <label className='block text-xs text-slate-400 mb-1'>Enviada em</label>
                    <input type='date' value={resultado.enviadaEm || ''} onChange={(e) => { setResultado({ ...resultado, enviadaEm: e.target.value }); setResultadoDirty(true); }} className={INPUT} />
                  </div>
                </div>
                <div>
                  <label className='block text-xs text-slate-400 mb-1'>Objeção que apareceu</label>
                  <textarea rows={3} value={resultado.objecao || ''} onChange={(e) => { setResultado({ ...resultado, objecao: e.target.value }); setResultadoDirty(true); }} className={INPUT} placeholder='O que o cliente disse, com as palavras dele' />
                </div>
                <div>
                  <label className='block text-xs text-slate-400 mb-1'>O que aprendemos (vira melhoria no núcleo?)</label>
                  <textarea rows={3} value={resultado.aprendizado || ''} onChange={(e) => { setResultado({ ...resultado, aprendizado: e.target.value }); setResultadoDirty(true); }} className={INPUT} placeholder='Regra, prova ou preço que faltou; frase que funcionou' />
                </div>
                {decidida && !resultado.objecao && !resultado.aprendizado && (
                  <div className='text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2'>A proposta foi {proposal.status === 'Aceita' ? 'aceita' : 'recusada'}. Registre a objeção e o aprendizado enquanto estão frescos.</div>
                )}
              </>
            )}
          </div>
        )}
    </CascaDeModal>
  );
}
